import { SAVE_VERSION } from '@/core/config';
import { DEFAULT_SETTINGS, type Facing, type Position, type Settings, type WeatherState } from '@/core/types';
import type { StorySnapshot } from '@/story/flags';

export interface SaveData {
  version: number;
  seed: number;
  createdAt: number;
  updatedAt: number;
  playtimeMs: number;
  story: StorySnapshot;
  position: Position | null;
  weather: WeatherState;
  settings: Settings;
}

/** Mirrors the core unions (tsc fails here when a state/facing is added there). */
const WEATHER_STATES: Record<WeatherState, true> = { sultry: true, wind: true, drizzle: true, rain: true, 'interior-rain': true };
const FACINGS: Record<Facing, true> = { down: true, left: true, right: true, up: true };

export function isWeatherState(x: unknown): x is WeatherState {
  return typeof x === 'string' && Object.hasOwn(WEATHER_STATES, x);
}
export function isFacing(x: unknown): x is Facing {
  return typeof x === 'string' && Object.hasOwn(FACINGS, x);
}
/** A resumable map position: non-empty map key, finite x/y, valid facing. */
export function isPosition(x: unknown): x is Position {
  if (!x || typeof x !== 'object') return false;
  const p = x as Record<string, unknown>;
  return typeof p['map'] === 'string' && p['map'].length > 0 && Number.isFinite(p['x']) && Number.isFinite(p['y']) && isFacing(p['facing']);
}

export type SaveSlot = 'auto' | 'manual-1';
export const SAVE_SLOTS: SaveSlot[] = ['auto', 'manual-1'];

export function newSave(seed: number, now: number): SaveData {
  return {
    version: SAVE_VERSION,
    seed,
    createdAt: now,
    updatedAt: now,
    playtimeMs: 0,
    story: { flags: [], marks: [] },
    position: null,
    weather: 'sultry',
    settings: { ...DEFAULT_SETTINGS },
  };
}

type Migration = (raw: Record<string, unknown>) => Record<string, unknown>;
/** version N -> N+1 migrations. Index 0 upgrades v0 (pre-release) to v1. */
export const MIGRATIONS: Migration[] = [
  (raw) => ({ ...raw, version: 1, story: raw['story'] ?? { flags: [], marks: [] } }),
];

/** Returns a valid SaveData or null when the input is unusable. Never throws. */
export function migrate(raw: unknown): SaveData | null {
  if (!raw || typeof raw !== 'object') return null;
  let data = { ...(raw as Record<string, unknown>) };
  let version = typeof data['version'] === 'number' ? (data['version'] as number) : 0;
  if (version > SAVE_VERSION) return null;
  while (version < SAVE_VERSION) {
    const m = MIGRATIONS[version];
    if (!m) return null;
    data = m(data);
    version++;
  }
  const story = data['story'] as Partial<StorySnapshot> | undefined;
  if (!story || !Array.isArray(story.flags)) return null;
  const rawSettings = data['settings'];
  const settings = { ...DEFAULT_SETTINGS, ...(rawSettings && typeof rawSettings === 'object' ? (rawSettings as Partial<Settings>) : {}) };
  const rawPos = data['position'];
  const position: Position | null = isPosition(rawPos) ? { map: rawPos.map, x: rawPos.x, y: rawPos.y, facing: rawPos.facing } : null;
  const rawWeather = data['weather'];
  return {
    version: SAVE_VERSION,
    seed: typeof data['seed'] === 'number' ? (data['seed'] as number) : 1157,
    createdAt: typeof data['createdAt'] === 'number' ? (data['createdAt'] as number) : 0,
    updatedAt: typeof data['updatedAt'] === 'number' ? (data['updatedAt'] as number) : 0,
    playtimeMs: typeof data['playtimeMs'] === 'number' ? (data['playtimeMs'] as number) : 0,
    story: { flags: story.flags.filter((f): f is StorySnapshot['flags'][number] => typeof f === 'string'), marks: Array.isArray(story.marks) ? story.marks.filter((m): m is string => typeof m === 'string') : [] },
    position,
    weather: isWeatherState(rawWeather) ? rawWeather : 'sultry',
    settings,
  };
}
